// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, Image, ImageBackground, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { createAppLogger } from '@/services/appLogger';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { useRoundState } from '@/stores/roundState';
import { hasClaimAccess } from '@/services/claimHttp';
import {
  createBffWebSocket,
  type HoleUpdateEvent,
  type TourMemberClaimedEvent,
  type TourMemberClaimRejectedEvent,
  type TourMemberClaimReleasedEvent,
  type TourLeaderboardErrorEvent,
  type TourLeaderboardStateEvent,
  type TourLeaderboardUpdateEvent,
} from '@/services/bffWebSocket';
import {
  clearActiveClaimSession,
  ensureClaimClientId,
  getActiveClaimSessionSync,
  getClaimClientIdSync,
  loadClaimSession,
  setActiveClaimSession,
  subscribeClaimSession,
} from '@/services/claimSession';
import { ResultsGraph } from '@/features/tour-leaderboard/components/ResultsGraph';
import { ResultsTable } from '@/features/tour-leaderboard/components/ResultsTable';
import { styles } from '@/features/tour-leaderboard/styles';
import type {
  GameDefinition,
  Player,
  RoundSummary,
  TourGameScore,
  TourRoundGameScore,
  TourTotalsByHoleResponse,
} from '@/features/tour-leaderboard/types';
import appColors from '@/theme/colors';
import {
  GAME_COL_WIDTH,
  buildOrderedRounds,
  buildPlayerRows,
} from '@/features/tour-leaderboard/utils';

type LeaderboardWsLifecycle = 'connecting' | 'connected-waiting-data' | 'received' | 'error';

const isRoundSelectableByStatus = (status: string | undefined): boolean => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return (
    normalized === 'open' ||
    normalized === 'active' ||
    normalized === 'playing' ||
    normalized === 'started' ||
    normalized === 'finished'
  );
};

export default function TourLeaderboardScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam, inviteToken: inviteTokenParam } = useLocalSearchParams<{ tourId?: string; inviteToken?: string }>();
  const normalizedInviteToken = useMemo(() => String(inviteTokenParam ?? '').trim(), [inviteTokenParam]);
  const [tourId, setTourId] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [tourImageKey, setTourImageKey] = useState<string | null>(null);
  const [roundCount, setRoundCount] = useState<number | null>(null);
  const [scoreboard, setScoreboard] = useState<TourGameScore[]>([]);
  const [roundScoreboard, setRoundScoreboard] = useState<TourRoundGameScore[]>([]);
  const [fallbackTotals, setFallbackTotals] = useState<Array<{ playerId: string; totalPoints: number }>>([]);
  const [gameDefs, setGameDefs] = useState<GameDefinition[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [roundMembersByRoundId, setRoundMembersByRoundId] = useState<Record<string, string[]>>({});
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [totalsByHole, setTotalsByHole] = useState<TourTotalsByHoleResponse | null>(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsTableOpen, setResultsTableOpen] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [claimRoutePending, setClaimRoutePending] = useState(false);
  const [claimedSourcePlayerId, setClaimedSourcePlayerId] = useState<string | null>(null);
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [claimClientId, setClaimClientId] = useState<string | null>(() => getClaimClientIdSync());
  const [claimWaitingForRoundSetup, setClaimWaitingForRoundSetup] = useState(false);
  const [pendingClaimRequestByPlayerId, setPendingClaimRequestByPlayerId] = useState<Record<string, string>>({});
  const [wsLifecycle, setWsLifecycle] = useState<LeaderboardWsLifecycle>('connecting');
  const [retryCount, setRetryCount] = useState(0);
  const [liveClaimTokenOverride, setLiveClaimTokenOverride] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const hasRouteInviteToken = !!normalizedInviteToken;
  const isClaimGuestMode = !user?.token && hasClaimAccess(claimSession);
  const showInviteClaimUi = !user?.token && hasRouteInviteToken;
  const effectiveInviteToken = useMemo(
    () => normalizedInviteToken || claimSession?.inviteToken?.trim() || '',
    [claimSession?.inviteToken, normalizedInviteToken]
  );
  const allowAnonymousTourAccess = !user?.token && (!!effectiveInviteToken || isClaimGuestMode);
  const claimTokenForSocket = useMemo(
    () => liveClaimTokenOverride ?? (isClaimGuestMode ? (claimSession?.claimAccessToken ?? null) : null),
    [claimSession?.claimAccessToken, isClaimGuestMode, liveClaimTokenOverride]
  );
  const socketTourSubscriptionId = useMemo(
    () => (allowAnonymousTourAccess || !!claimTokenForSocket ? null : (tourId ?? null)),
    [allowAnonymousTourAccess, claimTokenForSocket, tourId]
  );
  const gameDefsRef = useRef<GameDefinition[]>([]);
  const claimRoutePendingRef = useRef(false);
  const claimWaitingForRoundSetupRef = useRef(false);
  const claimedSourcePlayerIdRef = useRef<string | null>(null);
  const setRoundAndHole = useRoundState((state) => state.setRoundAndHole);
  const setScorerOnlyMode = useRoundState((state) => state.setScorerOnlyMode);
  const setScorerPlayerId = useRoundState((state) => state.setScorerPlayerId);
  const socketRef = useRef<ReturnType<typeof createBffWebSocket> | null>(null);
  if (!socketRef.current) {
    socketRef.current = createBffWebSocket(apiBaseUrl);
  }
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tour-leaderboard',
      }, socketRef.current),
    [apiBaseUrl, user?.token]
  );
  const pageBackgroundImage = useMemo(
    () => getAssetImageSource('images/tour-leader-board.png', 'hero', '/tour/[tourId]/leaderboard'),
    []
  );
  const trophyImage = useMemo(() => getAssetImageSource('images/trophy.png', 'icon', '/tour/[tourId]/leaderboard'), []);
  const logClaimConsole = useCallback((event: string, payload: Record<string, unknown>) => {
    if (typeof window === 'undefined') {
      return;
    }

    const output = {
      ...payload,
      inviteToken: effectiveInviteToken || null,
      tourId: tourId ?? null,
      claimClientId: claimClientId ?? null,
      userId: user?.id ?? null,
    };

    if (event === 'claim.rejected' || event === 'claim.send.failed' || event === 'claim.send.skipped') {
      console.warn(`[tour-leaderboard] ${event}`, output);
      return;
    }

    console.info(`[tour-leaderboard] ${event}`, output);
  }, [claimClientId, effectiveInviteToken, tourId, user?.id]);
  const tourImageUrl = useMemo(() => {
    if (!tourImageKey || !tourId) return null;
    if (tourImageKey.startsWith('http')) return tourImageKey;
    return `${apiBaseUrl}/api/tours/${tourId}/image`;
  }, [apiBaseUrl, tourId, tourImageKey]);

  useEffect(() => {
    const resolved = tourParam ?? claimSession?.tourId ?? (effectiveInviteToken ? null : getCurrentTourId());
    setTourId(resolved ?? null);
    if (resolved) {
      setCurrentTourId(resolved);
    }
  }, [claimSession?.tourId, effectiveInviteToken, tourParam]);

  const buildRequestId = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `tour-lb-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    ensureClaimClientId()
      .then((resolvedClaimClientId) => {
        if (!cancelled) {
          setClaimClientId(resolvedClaimClientId);
        }
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    return () => {
      socket?.dispose();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    claimRoutePendingRef.current = claimRoutePending;
  }, [claimRoutePending]);

  useEffect(() => {
    claimWaitingForRoundSetupRef.current = claimWaitingForRoundSetup;
  }, [claimWaitingForRoundSetup]);

  useEffect(() => {
    claimedSourcePlayerIdRef.current = claimedSourcePlayerId;
  }, [claimedSourcePlayerId]);
  useEffect(() => {
    gameDefsRef.current = gameDefs;
  }, [gameDefs]);

  useEffect(() => {
    if (!user?.token && !claimSessionLoaded) return;
    if (!tourId && !effectiveInviteToken) return;
    if (!claimClientId) return;
    const socket = socketRef.current;
    const requestedTourId = tourId ? String(tourId).toLowerCase() : null;
    const isAnonymousInviteView = allowAnonymousTourAccess;
    const startedAtMs = Date.now();
    const retryIntervalMs = 5000;
    const disconnectedErrorAfterMs = 20000;
    let snapshotReceived = false;
    let disconnectedSinceMs: number | null = null;
    let hasRequestedSnapshotSinceConnect = false;
    let retries = 0;
    let hasAdvancedAfterClaim = false;

    const normalizeId = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const resolveClaimedPlayerName = (sourcePlayerId?: string | null) => {
      const normalizedSourcePlayerId = normalizeId(sourcePlayerId ?? '');
      if (!normalizedSourcePlayerId) {
        return claimSession?.sourcePlayerName ?? null;
      }

      return (
        players.find((player) => normalizeId(player.id) === normalizedSourcePlayerId)?.name ??
        claimSession?.sourcePlayerName ??
        null
      );
    };

    const routeToHoleOne = (
      resolvedRoundId: string,
      sourcePlayerId?: string | null,
      resolvedTourId?: string | null
    ) => {
      if (hasAdvancedAfterClaim) {
        return;
      }
      hasAdvancedAfterClaim = true;
      setRoundAndHole(resolvedRoundId, 1);
      setScorerOnlyMode(true);
      setScorerPlayerId(sourcePlayerId ?? null);
      setClaimedSourcePlayerId(sourcePlayerId ?? null);
      claimedSourcePlayerIdRef.current = sourcePlayerId ?? null;
      setClaimWaitingForRoundSetup(false);
      claimWaitingForRoundSetupRef.current = false;
      setClaimRoutePending(false);
      claimRoutePendingRef.current = false;
      const latestClaimSession = getActiveClaimSessionSync();
      const nextClaimSession = {
        claimClientId,
        tourId: resolvedTourId ?? tourId ?? requestedTourId ?? '',
        sourcePlayerId: sourcePlayerId ?? null,
        sourcePlayerName: resolveClaimedPlayerName(sourcePlayerId),
        startedRoundId: resolvedRoundId,
        inviteToken: effectiveInviteToken || latestClaimSession?.inviteToken || null,
        claimAccessToken: latestClaimSession?.claimAccessToken ?? claimSession?.claimAccessToken ?? null,
        claimedAt: new Date().toISOString(),
      };
      setClaimSession(nextClaimSession);
      void setActiveClaimSession(nextClaimSession);
      const params = new URLSearchParams();
      params.set('scorerOnly', 'true');
      if (sourcePlayerId) {
        params.set('playerId', sourcePlayerId);
      }
      const selectedGameIds = gameDefsRef.current
        .map((game) => normalizeId(game.id))
        .filter((id) => !!id);
      if (selectedGameIds.length > 0) {
        params.set('gameDefinitionIds', selectedGameIds.join(','));
      }
      const query = `?${params.toString()}`;
      router.push(`/round/${resolvedRoundId}/hole/1/compact${query}`);
    };

    socket.batchUpdate(() => {
      socket.setInviteToken(effectiveInviteToken || null);
      socket.setClaimClientId(claimClientId);
      socket.setClaimAccessToken(claimTokenForSocket);
      socket.setSubscriptionContext({ tourId: socketTourSubscriptionId });
      if (user?.token) {
        socket.setAuthToken(user.token);
        socket.setAnonymousReadOnly(false);
        return;
      }

      socket.setAnonymousReadOnly(isAnonymousInviteView);
      socket.setAuthToken(null);
    });
    setLoading(true);
    setError(null);
    setRetryCount(0);
    setWsLifecycle('connecting');

    const normalizeTour = (rawTour: Record<string, unknown>) => {
      const tourName = (rawTour.tourName as string | undefined) ?? (rawTour.name as string | undefined) ?? null;
      const imageThumbKey =
        (rawTour.imageThumbKey as string | undefined) ??
        (rawTour.ImageThumbKey as string | undefined) ??
        null;
      const imageKey = (rawTour.imageKey as string | undefined) ?? (rawTour.ImageKey as string | undefined) ?? null;
      setTourName(tourName);
      setTourImageKey(imageThumbKey ?? imageKey);
    };

    const normalizeRounds = (rawRounds: Array<Record<string, unknown>>) => {
      const normalized = rawRounds.map((round) => ({
        id: normalizeId((round.id as string | undefined) ?? (round.Id as string | undefined) ?? ''),
        courseName: (round.courseName as string | undefined) ?? (round.CourseName as string | undefined),
        name: (round.name as string | undefined) ?? (round.Name as string | undefined),
        description: (round.description as string | undefined) ?? (round.Description as string | undefined),
        status: (round.status as string | undefined) ?? (round.Status as string | undefined),
        createdAt: (round.createdAt as string | undefined) ?? (round.CreatedAt as string | undefined),
      }));
      setRounds(normalized.filter((round) => !!round.id));
      setRoundCount(normalized.length);
    };

    const resolvePlayableRoundId = (rawRounds: Array<Record<string, unknown>>) => {
      const normalized = rawRounds
        .map((round) => ({
          id: normalizeId((round.id as string | undefined) ?? (round.Id as string | undefined) ?? ''),
          status: String((round.status as string | undefined) ?? (round.Status as string | undefined) ?? '').toLowerCase(),
        }))
        .filter((round) => !!round.id);

      const activeRound = normalized.find((round) =>
        round.status === 'active' || round.status === 'playing' || round.status === 'started'
      );
      if (activeRound) {
        return activeRound.id;
      }

      const openRound = normalized.find((round) => round.status === 'open');
      return openRound?.id ?? null;
    };

    const normalizePlayers = (rawPlayers: Array<Record<string, unknown>>) => {
      const normalized = rawPlayers.map((player) => ({
        id: normalizeId((player.id as string | undefined) ?? (player.Id as string | undefined) ?? ''),
        name: (player.name as string | undefined) ?? (player.Name as string | undefined) ?? '',
        userId: (player.userId as string | undefined) ?? (player.UserId as string | undefined) ?? null,
        joinedAt: (player.joinedAt as string | undefined) ?? (player.JoinedAt as string | undefined),
        isTemporary: Boolean(
          (player.isTemporary as boolean | undefined) ??
            (player.IsTemporary as boolean | undefined) ??
            false
        ),
      }));
      setPlayers(normalized.filter((player) => !!player.id));
    };

    const normalizeGames = (rawGames: Array<Record<string, unknown>>) => {
      const normalized = rawGames.map((game) => ({
        id: normalizeId((game.id as string | undefined) ?? (game.Id as string | undefined) ?? ''),
        name: (game.name as string | undefined) ?? (game.Name as string | undefined),
      }));
      setGameDefs(normalized.filter((game) => !!game.id));
    };

    const normalizeScoresByTourGame = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows.map((row) => ({
        playerId: normalizeId((row.playerId as string | undefined) ?? (row.PlayerId as string | undefined) ?? ''),
        gameId: normalizeId((row.gameId as string | undefined) ?? (row.GameId as string | undefined) ?? ''),
        scorePoints: Number((row.scorePoints as number | undefined) ?? (row.ScorePoints as number | undefined) ?? 0),
      }));
      setScoreboard(normalized.filter((row) => !!row.playerId && !!row.gameId));
    };

    const normalizeScoresByRoundGame = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows.map((row) => ({
        roundId: normalizeId((row.roundId as string | undefined) ?? (row.RoundId as string | undefined) ?? ''),
        courseName: (row.courseName as string | undefined) ?? (row.CourseName as string | undefined),
        playerId: normalizeId((row.playerId as string | undefined) ?? (row.PlayerId as string | undefined) ?? ''),
        gameId: normalizeId((row.gameId as string | undefined) ?? (row.GameId as string | undefined) ?? ''),
        scorePoints: Number(
          (row.scorePoints as number | undefined) ??
            (row.ScorePoints as number | undefined) ??
            (row.roundGamePoints as number | undefined) ??
            (row.RoundGamePoints as number | undefined) ??
            (row.gamesPoints as number | undefined) ??
            (row.GamesPoints as number | undefined) ??
            (row.points as number | undefined) ??
            (row.Points as number | undefined) ??
            0
        ),
      }));
      setRoundScoreboard(normalized.filter((row) => !!row.roundId && !!row.playerId));
    };

    const normalizeTotals = (rows: Array<Record<string, unknown>>) => {
      const normalized = rows
        .map((entry) => ({
          playerId: normalizeId((entry.playerId as string | undefined) ?? (entry.PlayerId as string | undefined) ?? ''),
          totalPoints: Number((entry.totalPoints as number | undefined) ?? (entry.TotalPoints as number | undefined) ?? 0),
        }))
        .filter((entry) => !!entry.playerId);
      setFallbackTotals(normalized);
    };

    const normalizeRoundMembersByRound = (rawMap: Record<string, string[]> | undefined) => {
      if (!rawMap || typeof rawMap !== 'object') {
        setRoundMembersByRoundId({});
        return;
      }

      const normalized = Object.entries(rawMap).reduce<Record<string, string[]>>((acc, [roundId, playerIds]) => {
        const normalizedRoundId = normalizeId(roundId);
        if (!normalizedRoundId) {
          return acc;
        }

        const ids = Array.isArray(playerIds)
          ? Array.from(new Set(playerIds.map((value) => normalizeId(value)).filter((value) => !!value)))
          : [];

        acc[normalizedRoundId] = ids;
        return acc;
      }, {});

      setRoundMembersByRoundId(normalized);
    };

    const applySnapshot = (message: TourLeaderboardStateEvent) => {
      const incomingTourId = normalizeId(message.tourId);
      if (!incomingTourId) {
        return;
      }
      if (requestedTourId && incomingTourId !== requestedTourId) {
        logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: requestedTourId, messageTourId: message.tourId, type: message.type }, 'debug');
        return;
      }
      if (!allowAnonymousTourAccess) {
        setTourId((current) => current ?? message.tourId ?? null);
      }
      normalizeTour((message.tour ?? {}) as Record<string, unknown>);
      const rawRounds = (message.rounds ?? []) as Array<Record<string, unknown>>;
      normalizeRounds(rawRounds);
      normalizePlayers((message.players ?? []) as Array<Record<string, unknown>>);
      normalizeGames((message.games ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByTourGame((message.scoresByTourGame ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByRoundGame((message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>);
      normalizeTotals((message.totals ?? []) as Array<Record<string, unknown>>);
      normalizeRoundMembersByRound((message.roundMembersByRoundId ?? {}) as Record<string, string[]>);
      setTotalsByHole((message.totalsByHole as TourTotalsByHoleResponse | undefined) ?? null);
      if (claimRoutePendingRef.current && claimWaitingForRoundSetupRef.current) {
        const fallbackRoundId = resolvePlayableRoundId(rawRounds);
        if (fallbackRoundId) {
          routeToHoleOne(
            fallbackRoundId,
            claimedSourcePlayerIdRef.current,
            message.tourId ?? null
          );
        }
      }
      if (!snapshotReceived) {
        const firstSnapshotLatencyMs = Date.now() - startedAtMs;
        logToBff('tourLeaderboard.ws.firstSnapshot', { tourId: incomingTourId, latencyMs: firstSnapshotLatencyMs }, 'debug');
      }
      snapshotReceived = true;
      setError(null);
      setLoading(false);
      setWsLifecycle('received');
    };

    const applyUpdate = (message: TourLeaderboardUpdateEvent) => {
      const incomingTourId = normalizeId(message.tourId);
      if (!incomingTourId) {
        return;
      }
      if (requestedTourId && incomingTourId !== requestedTourId) {
        logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: requestedTourId, messageTourId: message.tourId, type: message.type }, 'debug');
        return;
      }
      if (!allowAnonymousTourAccess) {
        setTourId((current) => current ?? message.tourId ?? null);
      }
      normalizeScoresByTourGame((message.scoresByTourGame ?? []) as Array<Record<string, unknown>>);
      normalizeScoresByRoundGame((message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>);
      normalizeTotals((message.totals ?? []) as Array<Record<string, unknown>>);
      snapshotReceived = true;
      setError(null);
      setLoading(false);
      setWsLifecycle('received');
    };

    const unsubscribeMessage = socket.onMessage((message) => {
      if (message.type === 'tour.leaderboard.state') {
        applySnapshot(message as TourLeaderboardStateEvent);
      } else if (message.type === 'tour.leaderboard.update') {
        applyUpdate(message as TourLeaderboardUpdateEvent);
      } else if (message.type === 'tour.leaderboard.error') {
        const err = message as TourLeaderboardErrorEvent;
        if (requestedTourId && err.tourId && normalizeId(err.tourId) !== requestedTourId) {
          logToBff('tourLeaderboard.ws.dropTourMismatch', { expectedTourId: requestedTourId, messageTourId: err.tourId, type: message.type }, 'debug');
          return;
        }
        const code = String(err.code ?? '').trim();
        const isTerminal = code === 'tour.leaderboard.invalidTourId' || code === 'tour.leaderboard.invalidInviteToken';
        if (isTerminal) {
          setError(t('tourLeaderboard.loadError'));
          setLoading(false);
          setWsLifecycle('error');
        } else {
          // Keep waiting and let interval/connection retries request a fresh snapshot.
          setError(null);
          setLoading(true);
          setWsLifecycle('connected-waiting-data');
        }
        logToBff('tourLeaderboard.ws.error', { tourId: requestedTourId ?? err.tourId, code: err.code, message: err.message, terminal: isTerminal }, 'warn');
      } else if (message.type === 'tour.member.claimed') {
        const claim = message as TourMemberClaimedEvent;
        if (claim.tourId && requestedTourId && normalizeId(claim.tourId) !== requestedTourId) {
          return;
        }
        const ownsClaimEvent = !!claim.claimClientId && normalizeId(claim.claimClientId) === normalizeId(claimClientId);
        if (!ownsClaimEvent) {
          requestSnapshot('claim-observed');
          return;
        }
        if (claim.sourcePlayerId) {
          setClaimedSourcePlayerId(claim.sourcePlayerId);
          claimedSourcePlayerIdRef.current = claim.sourcePlayerId;
        }

        if (!claim.claimToken) {
          logClaimConsole('claim.missingToken', {
            requestId: claim.requestId ?? null,
            sourcePlayerId: claim.sourcePlayerId ?? null,
            startedRoundId: claim.startedRoundId ?? null,
            claimedAt: claim.claimedAt ?? null,
          });
          setPendingClaimRequestByPlayerId((pending) => {
            if (!claim.sourcePlayerId || !pending[claim.sourcePlayerId]) {
              return pending;
            }

            const next = { ...pending };
            delete next[claim.sourcePlayerId];
            return next;
          });
          setClaimRoutePending(false);
          claimRoutePendingRef.current = false;
          setClaimWaitingForRoundSetup(false);
          claimWaitingForRoundSetupRef.current = false;
          setClaimMessage(t('tourLeaderboard.claimTokenMissing'));
          setLiveClaimTokenOverride(null);
          setClaimSession(null);
          void clearActiveClaimSession();
          return;
        }

        setPendingClaimRequestByPlayerId((pending) => {
          if (!claim.sourcePlayerId || !pending[claim.sourcePlayerId]) {
            return pending;
          }

          const next = { ...pending };
          delete next[claim.sourcePlayerId];
          return next;
        });
        const nextClaimSession = {
          claimClientId,
          tourId: claim.tourId ?? tourId ?? requestedTourId ?? '',
          sourcePlayerId: claim.sourcePlayerId ?? null,
          sourcePlayerName: resolveClaimedPlayerName(claim.sourcePlayerId),
          startedRoundId: claim.startedRoundId ?? null,
          inviteToken: effectiveInviteToken || null,
          claimAccessToken: claim.claimToken ?? null,
          claimedAt: claim.claimedAt ?? new Date().toISOString(),
        };
        setLiveClaimTokenOverride(claim.claimToken);

        socket.batchUpdate(() => {
          socket.setInviteToken(effectiveInviteToken || null);
          socket.setClaimClientId(claim.claimClientId ?? claimClientId ?? null);
          socket.setClaimAccessToken(claim.claimToken);
          socket.setSubscriptionContext({ tourId: null });
          socket.setAuthToken(null);
          socket.setAnonymousReadOnly(true);
        });

        setClaimSession(nextClaimSession);
        void setActiveClaimSession(nextClaimSession);
        logClaimConsole('claim.accepted', {
          requestId: claim.requestId ?? null,
          sourcePlayerId: claim.sourcePlayerId ?? null,
          startedRoundId: claim.startedRoundId ?? null,
          hasClaimToken: !!claim.claimToken,
          claimedAt: claim.claimedAt ?? null,
        });

        if (claim.startedRoundId) {
          routeToHoleOne(
            claim.startedRoundId,
            claim.sourcePlayerId ?? null,
            claim.tourId ?? null
          );
        } else {
          setClaimMessage(t('tourLeaderboard.claimSuccess'));
          setClaimWaitingForRoundSetup(true);
          claimWaitingForRoundSetupRef.current = true;
          setClaimRoutePending(true);
          claimRoutePendingRef.current = true;
        }
        requestSnapshot('claim-success');
      } else if (message.type === 'tour.member.claim.rejected') {
        const claimRejected = message as TourMemberClaimRejectedEvent;
        if (claimRejected.tourId && requestedTourId && normalizeId(claimRejected.tourId) !== requestedTourId) {
          return;
        }
        if (claimRejected.claimClientId && normalizeId(claimRejected.claimClientId) !== normalizeId(claimClientId)) {
          return;
        }

        setPendingClaimRequestByPlayerId((pending) => {
          if (!claimRejected.sourcePlayerId || !pending[claimRejected.sourcePlayerId]) {
            return pending;
          }

          const next = { ...pending };
          delete next[claimRejected.sourcePlayerId];
          return next;
        });
        const translated = claimRejected.messageKey ? t(claimRejected.messageKey) : null;
        logClaimConsole('claim.rejected', {
          requestId: claimRejected.requestId ?? null,
          sourcePlayerId: claimRejected.sourcePlayerId ?? null,
          code: claimRejected.code ?? null,
          messageKey: claimRejected.messageKey ?? null,
          message: claimRejected.message ?? null,
          rejectedAt: claimRejected.rejectedAt ?? null,
        });
        setClaimMessage(
          translated && claimRejected.messageKey && translated !== claimRejected.messageKey
            ? translated
            : claimRejected.message ?? t('tourLeaderboard.claimRejected'));
        setClaimRoutePending(false);
        claimRoutePendingRef.current = false;
        setClaimWaitingForRoundSetup(false);
        claimWaitingForRoundSetupRef.current = false;
        requestSnapshot('claim-rejected');
      } else if (message.type === 'tour.member.claim.released') {
        const claimReleased = message as TourMemberClaimReleasedEvent;
        if (claimReleased.tourId && requestedTourId && normalizeId(claimReleased.tourId) !== requestedTourId) {
          return;
        }
        const ownsReleaseEvent = !!claimReleased.claimClientId
          && normalizeId(claimReleased.claimClientId) === normalizeId(claimClientId);
        if (!ownsReleaseEvent) {
          requestSnapshot('claim-release-observed');
          return;
        }
        setPendingClaimRequestByPlayerId({});
        setClaimRoutePending(false);
        claimRoutePendingRef.current = false;
        setClaimWaitingForRoundSetup(false);
        claimWaitingForRoundSetupRef.current = false;
        setLiveClaimTokenOverride(null);
        setClaimedSourcePlayerId(null);
        claimedSourcePlayerIdRef.current = null;
        setClaimMessage(t('tourLeaderboard.claimReleased'));
        setClaimSession(null);
        void clearActiveClaimSession();
      } else if (message.type === 'hole.update') {
        const holeUpdate = message as HoleUpdateEvent;
        if (!claimRoutePendingRef.current || !claimWaitingForRoundSetupRef.current) {
          return;
        }
        const updateTourId = normalizeId(holeUpdate.tourId ?? '');
        if (requestedTourId && updateTourId && updateTourId !== requestedTourId) {
          return;
        }
        if (holeUpdate.hole !== 1) {
          return;
        }
        if (!holeUpdate.entries?.length || !holeUpdate.roundId) {
          return;
        }
        routeToHoleOne(
          holeUpdate.roundId,
          claimedSourcePlayerIdRef.current,
          holeUpdate.tourId ?? null
        );
      } else if (message.type === 'error') {
        const wsError = message as { code?: string; message?: string };
        if (wsError.code !== 'ws.anonymous.readOnly') {
          return;
        }
        setPendingClaimRequestByPlayerId({});
        setClaimMessage(wsError.message ?? t('tourLeaderboard.claimRejected'));
      }
    });

    const requestSnapshot = (reason: string) => {
      hasRequestedSnapshotSinceConnect = true;
      const requestId = buildRequestId();
      socket.sendTourLeaderboardRequest({
        tourId: tourId ?? undefined,
        inviteToken: effectiveInviteToken || undefined,
        requestId,
      });
      logToBff('tourLeaderboard.ws.request', { tourId, inviteToken: effectiveInviteToken || null, requestId, reason, apiBaseUrl, retryCount: retries }, 'debug');
      if (typeof window !== 'undefined') {
        console.info('[tour-leaderboard] ws request', { tourId, inviteToken: effectiveInviteToken || null, requestId, reason, apiBaseUrl, retryCount: retries });
      }
    };

    requestSnapshot('initial');
    setWsLifecycle('connected-waiting-data');

    const retryInterval = setInterval(() => {
      if (snapshotReceived) {
        return;
      }

      retries += 1;
      setRetryCount(retries);
      setWsLifecycle('connected-waiting-data');
      requestSnapshot('interval-retry');
      logToBff('tourLeaderboard.ws.retry', { tourId, inviteToken: effectiveInviteToken || null, retryCount: retries }, 'debug');

      if (disconnectedSinceMs && Date.now() - disconnectedSinceMs >= disconnectedErrorAfterMs) {
        setError(t('tourLeaderboard.loadError'));
        setLoading(false);
        setWsLifecycle('error');
        logToBff('tourLeaderboard.ws.prolongedDisconnect', { tourId, inviteToken: effectiveInviteToken || null, disconnectedMs: Date.now() - disconnectedSinceMs }, 'warn');
      }
    }, retryIntervalMs);

    const unsubscribeConnection = socket.onConnectionChange((state) => {
      logToBff('tourLeaderboard.ws.connection', { tourId, inviteToken: effectiveInviteToken || null, state }, 'debug');
      if (state === 'connected') {
        disconnectedSinceMs = null;
        if (!snapshotReceived) {
          setWsLifecycle('connected-waiting-data');
        }
        if (!hasRequestedSnapshotSinceConnect) {
          requestSnapshot('connected');
        }
      } else if (!snapshotReceived) {
        hasRequestedSnapshotSinceConnect = false;
        if (!disconnectedSinceMs) {
          disconnectedSinceMs = Date.now();
        }
        setWsLifecycle('connecting');
      }
    });

    return () => {
      clearInterval(retryInterval);
      unsubscribeMessage();
      unsubscribeConnection();
      socket.batchUpdate(() => {
        socket.setSubscriptionContext({ roundId: null, tourId: null });
        socket.setInviteToken(null);
        socket.setClaimAccessToken(null);
        socket.setClaimClientId(null);
        if (!user?.token) {
          socket.setAnonymousReadOnly(false);
          socket.setAuthToken(null);
          return;
        }

        socket.setAnonymousReadOnly(false);
      });
    };
  }, [
    apiBaseUrl,
    buildRequestId,
    claimClientId,
    claimSessionLoaded,
    logToBff,
    effectiveInviteToken,
    claimTokenForSocket,
    allowAnonymousTourAccess,
    isClaimGuestMode,
    liveClaimTokenOverride,
    router,
    setRoundAndHole,
    setScorerOnlyMode,
    setScorerPlayerId,
    socketTourSubscriptionId,
    t,
    tourId,
    user?.token,
  ]);

  useEffect(() => {
    if (!claimSession?.claimAccessToken) {
      setLiveClaimTokenOverride((current) => (current ? null : current));
    }
  }, [claimSession?.claimAccessToken]);

  const wsStatusMessage = useMemo(() => {
    if (!loading || error) {
      return null;
    }

    if (wsLifecycle === 'connecting') {
      return t('tourLeaderboard.wsConnecting');
    }

    if (wsLifecycle === 'connected-waiting-data') {
      if (retryCount > 0) {
        return t('tourLeaderboard.wsRetrying', { count: retryCount });
      }

      return t('tourLeaderboard.wsWaitingData');
    }

    return null;
  }, [error, loading, retryCount, t, wsLifecycle]);

  const openResults = async () => {
    setResultsOpen(true);
    if (!totalsByHole) {
      setResultsError(t('tourLeaderboard.resultsError'));
      return;
    }
    setResultsLoading(false);
    setResultsError(null);
  };

  const formatRoundDate = (rawDate?: string) => {
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString();
  };

  useEffect(() => {
    setPendingClaimRequestByPlayerId({});
    setClaimMessage(null);
    setClaimRoutePending(false);
    claimRoutePendingRef.current = false;
    setClaimedSourcePlayerId(null);
    claimedSourcePlayerIdRef.current = null;
    setClaimWaitingForRoundSetup(false);
    claimWaitingForRoundSetupRef.current = false;
  }, [tourId, user?.id]);

  const handleClaimPlayer = (sourcePlayerId: string) => {
    if (!claimClientId) {
      logClaimConsole('claim.send.skipped', {
        sourcePlayerId,
        reason: 'missing-claim-client-id',
      });
      setClaimMessage(t('tourLeaderboard.claimSendError'));
      return;
    }

    const requestId = buildRequestId();
    logClaimConsole('claim.send', {
      sourcePlayerId,
      requestId,
      authenticated: !!user?.token,
    });
    setPendingClaimRequestByPlayerId((pending) => ({
      ...pending,
      [sourcePlayerId]: requestId,
    }));
    setClaimMessage(t('tourLeaderboard.claimPending'));

    const sent = socketRef.current.sendTourMemberClaim(
      tourId ?? undefined,
      sourcePlayerId,
      requestId,
      effectiveInviteToken || undefined
    );
    if (!sent) {
      setPendingClaimRequestByPlayerId((pending) => {
        const next = { ...pending };
        delete next[sourcePlayerId];
        return next;
      });
      logClaimConsole('claim.send.failed', {
        sourcePlayerId,
        requestId,
        reason: 'socket-send-returned-false',
      });
      setClaimMessage(t('tourLeaderboard.claimSendError'));
    }
  };

  const handleReleaseClaim = () => {
    if (!claimWaitingForRoundSetup || !claimClientId) {
      return;
    }

    const requestId = buildRequestId();
    const sent = socketRef.current.sendTourMemberClaimRelease(
      tourId ?? undefined,
      requestId,
      effectiveInviteToken || undefined
    );
    if (!sent) {
      setClaimMessage(t('tourLeaderboard.claimReleaseSendError'));
    }
  };

  if (!tourId && !effectiveInviteToken) {
    return (
      <View style={[styles.background, styles.container]}>
        <Text style={styles.title}>{t('tourLeaderboard.title')}</Text>
        <Text style={styles.meta}>{t('tourLeaderboard.missingTour')}</Text>
        <GoffyButton
          label={t('tourLeaderboard.goTours')}
          onPress={() => router.push('/tournaments')}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryText}
          style={{ width: 220, height: 60 }}
          textStyle={{ fontSize: 16 }}
        />
      </View>
    );
  }

  const showClaimSessionLoading = !user?.token && !claimSessionLoaded;
  const playerNameLookup = new Map(players.map((p) => [p.id, p.name]));
  const leaderboardPlayerIds = Array.from(
    new Set(
      [
        ...players.map((player) => player.id),
        ...scoreboard.map((entry) => entry.playerId),
        ...roundScoreboard.map((entry) => entry.playerId),
        ...fallbackTotals.map((entry) => entry.playerId),
      ].filter((id) => !!id)
    )
  );
  const playerRows = buildPlayerRows(
    scoreboard,
    playerNameLookup,
    t('tourLeaderboard.unknownPlayer'),
    leaderboardPlayerIds
  );
  const playerRowById = new Map(playerRows.map((row) => [row.playerId, row]));
  const fallbackTotalsByPlayerId = new Map(fallbackTotals.map((entry) => [entry.playerId, entry.totalPoints]));

  const orderedRounds = buildOrderedRounds(rounds);
  const roundIndexById = new Map(orderedRounds.map((round, index) => [round.id, index + 1]));
  const roundPointsByPlayerRound = new Map<string, number>();
  roundScoreboard.forEach((entry) => {
    if (!entry.playerId || !entry.roundId) {
      return;
    }
    const key = `${entry.playerId}:${entry.roundId}`;
    roundPointsByPlayerRound.set(key, (roundPointsByPlayerRound.get(key) ?? 0) + entry.scorePoints);
  });
  const totalPerRoundRows = playerRows.map((row) => ({
    playerId: row.playerId,
    name: row.name,
    total: fallbackTotalsByPlayerId.get(row.playerId) ?? row.total,
    roundPointsByRound: new Map(
      orderedRounds.map((round) => [
        round.id,
        roundPointsByPlayerRound.get(`${row.playerId}:${round.id}`) ?? 0,
      ])
    ),
  }));

  const roundRowsByRoundId = orderedRounds.reduce<Record<string, Array<{ playerId: string; name: string; roundPoints: number }>>>(
    (acc, round) => {
      const memberIds = roundMembersByRoundId[round.id] ?? [];
      const rows = memberIds
        .map((playerId) => ({
          playerId,
          name:
            playerNameLookup.get(playerId) ??
            playerRowById.get(playerId)?.name ??
            t('tourLeaderboard.unknownPlayer'),
          roundPoints: roundPointsByPlayerRound.get(`${playerId}:${round.id}`) ?? 0,
        }))
        .sort((a, b) => {
          if (b.roundPoints !== a.roundPoints) {
            return b.roundPoints - a.roundPoints;
          }
          return a.name.localeCompare(b.name);
        });

      acc[round.id] = rows;
      return acc;
    },
    {}
  );

  const tourMembersRows = players
    .map((player) => ({
      playerId: player.id,
      name: player.name || t('tourLeaderboard.unknownPlayer'),
      userId: player.userId,
      joinedAt: player.joinedAt,
      isTemporary: !!player.isTemporary || !player.userId,
    }))
    .sort((a, b) => {
      const activeClaimedPlayerId =
        claimedSourcePlayerId ?? claimSession?.sourcePlayerId ?? null;
      const aIsClaimed = !!activeClaimedPlayerId && a.playerId === activeClaimedPlayerId;
      const bIsClaimed = !!activeClaimedPlayerId && b.playerId === activeClaimedPlayerId;

      if (aIsClaimed && !bIsClaimed) {
        return -1;
      }

      if (!aIsClaimed && bIsClaimed) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });

  const membersRowsForView = tourMembersRows;
  const canUseClaimFeatures = !!user?.token || showInviteClaimUi;
  const hasRenderableData =
    membersRowsForView.length > 0 ||
    totalPerRoundRows.length > 0 ||
    rounds.length > 0 ||
    !!tourName;
  const effectiveSelectedTourId = tourId ?? claimSession?.tourId ?? null;
  const currentUserPlayerId =
    players.find((player) => !!user?.id && player.userId?.toLowerCase() === user.id.toLowerCase())?.id ?? null;
  const canClaimPlayer = (member: { playerId: string; isTemporary: boolean }) =>
    (!!tourId || (!!effectiveInviteToken && !user?.token)) &&
    canUseClaimFeatures &&
    !claimWaitingForRoundSetup &&
    member.isTemporary &&
    member.playerId !== currentUserPlayerId;

  const downloadCsv = async () => {
    if (!tourId || !user?.token) return;
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setError(t('tourLeaderboard.csvUnsupported'));
      logToBff('tourLeaderboard.csv.unsupported', { tourId }, 'warn');
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}/results.csv`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        setError(t('tourLeaderboard.csvError'));
        logToBff('tourLeaderboard.csv.error', { tourId, status: response.status }, 'warn');
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') ?? '';
      const fileMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const filename = fileMatch?.[1] ?? `tour-results.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      logToBff('tourLeaderboard.csv.downloaded', { tourId, filename });
    } catch (err) {
      setError(t('tourLeaderboard.csvError'));
      logToBff('tourLeaderboard.csv.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
    }
  };

  return (
    <ImageBackground
      source={pageBackgroundImage}
      style={styles.background}
      imageStyle={styles.backgroundImage}
      resizeMode="cover"
    >
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        <View style={styles.headerCard}>
          {tourImageUrl ? (
            <Image source={{ uri: tourImageUrl }} style={styles.headerImage} resizeMode="cover" />
          ) : (
            <Image source={trophyImage} style={styles.headerImage} resizeMode="contain" />
          )}
          <View style={styles.headerText}>
            <Text style={styles.bannerTitle}>{tourName ?? t('tourLeaderboard.title')}</Text>
            {roundCount !== null ? <Text style={styles.bannerMeta}>{t('tourLeaderboard.roundsTitle')}: {roundCount}</Text> : null}
          </View>
        </View>
        {!showInviteClaimUi && orderedRounds.length ? (
          <View style={styles.roundPickerCard}>
            <Text style={styles.roundPickerTitle}>{t('scorecard.selectRoundTitle')}</Text>
            <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
              <Text style={styles.roundPickerText} numberOfLines={1}>
                {t('tourLeaderboard.roundSectionsTitle')}
              </Text>
              <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
            </Pressable>
          </View>
        ) : null}
        {showClaimSessionLoading ? (
          <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
            <ActivityIndicator color={appColors.text.primary} />
          </View>
        ) : (
          <>
            {loading && !hasRenderableData ? <ActivityIndicator color={appColors.text.primary} /> : null}
            {loading && !hasRenderableData && wsStatusMessage ? <Text style={styles.meta}>{wsStatusMessage}</Text> : null}
            {error ? <Text style={styles.error}>{error}</Text> : null}
            {claimWaitingForRoundSetup ? (
              <View style={styles.waitingCard}>
                <Text style={styles.waitingTitle}>{t('tourLeaderboard.waitingForRoundSetupTitle')}</Text>
                <Text style={styles.waitingText}>{t('tourLeaderboard.waitingForRoundSetup')}</Text>
                <ActivityIndicator color={appColors.text.primary} />
                <GoffyButton
                  label={t('tourLeaderboard.claimReleaseCta')}
                  onPress={handleReleaseClaim}
                  size="compact"
                  tone="dark"
                  oldButtonStyle={styles.resultsActionButton}
                  oldTextStyle={styles.resultsActionButtonText}
                  style={styles.resultsActionButton}
                  textStyle={styles.resultsActionButtonText}
                />
              </View>
            ) : null}
            {!claimWaitingForRoundSetup && claimMessage ? <Text style={styles.meta}>{claimMessage}</Text> : null}
            {!loading && !error && !totalPerRoundRows.length && !tourMembersRows.length ? (
              <Text style={styles.meta}>{t('tourLeaderboard.empty')}</Text>
            ) : null}
            {!showInviteClaimUi && totalPerRoundRows.length ? (
              <View style={styles.tableCard}>
                <View style={styles.tableHeaderRow}>
                  <Text style={styles.tableHeaderTitle}>{t('tourLeaderboard.resultsHeader')}</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.table}>
                  <View>
                    <View style={styles.tableHeaderTopRow}>
                      <Text style={[styles.cell, styles.playerCell, styles.playerHeaderCell]}>{t('tourLeaderboard.player')}</Text>
                      <Text style={[styles.cell, styles.totalCell, styles.totalCellHeader]}>{t('tourLeaderboard.total')}</Text>
                      <View
                        style={[
                          styles.gameHeaderCell,
                          styles.roundGroupHeaderCell,
                          { width: Math.max(1, orderedRounds.length) * GAME_COL_WIDTH },
                        ]}
                      >
                        <Text style={styles.gameHeaderText} numberOfLines={1}>
                          {t('tourLeaderboard.roundRowLabel')}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.tableHeaderBottomRow}>
                      <View style={styles.roundHeaderOffset} />
                      {orderedRounds.map((round) => (
                        <View key={`${round.id}-header`} style={[styles.gameHeaderCell, styles.roundCell, styles.roundHeaderCell]}>
                          <Text style={styles.gameHeaderText} numberOfLines={1}>
                            {roundIndexById.get(round.id) ?? '-'}
                          </Text>
                        </View>
                      ))}
                    </View>
                    {totalPerRoundRows.map((row, index) => (
                      <View key={row.playerId} style={[styles.tableRow, index % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                        <Text style={[styles.cell, styles.playerCell, styles.resultCell]} numberOfLines={1}>
                          {row.name}
                        </Text>
                        <Text style={[styles.cell, styles.totalCell, styles.resultCell, styles.resultText]}>{row.total}</Text>
                        {orderedRounds.map((round) => (
                          <Text
                            key={`${row.playerId}-${round.id}`}
                            style={[styles.cell, styles.roundCell, styles.resultCell, styles.resultText]}
                          >
                            {row.roundPointsByRound.get(round.id) ?? 0}
                          </Text>
                        ))}
                      </View>
                    ))}
                  </View>
                </ScrollView>
                <View style={styles.resultsButtonRow}>
                  <GoffyButton
                    label={t('tourLeaderboard.resultsButton')}
                    onPress={openResults}
                    size="compact"
                    tone="dark"
                    oldButtonStyle={styles.resultsActionButton}
                    oldTextStyle={styles.resultsActionButtonText}
                    style={styles.resultsActionButton}
                    textStyle={styles.resultsActionButtonText}
                  />
                  <GoffyButton
                    label={t('tourLeaderboard.resultsTableButton')}
                    onPress={() => setResultsTableOpen(true)}
                    size="compact"
                    tone="dark"
                    oldButtonStyle={styles.resultsActionButton}
                    oldTextStyle={styles.resultsActionButtonText}
                    style={styles.resultsActionButton}
                    textStyle={styles.resultsActionButtonText}
                  />
                  {user?.token ? (
                    <GoffyButton
                      label={t('tourLeaderboard.downloadCsv')}
                      onPress={downloadCsv}
                      size="compact"
                      tone="dark"
                      oldButtonStyle={styles.resultsActionButton}
                      oldTextStyle={styles.resultsActionButtonText}
                      style={styles.resultsActionButton}
                      textStyle={styles.resultsActionButtonText}
                    />
                  ) : null}
                </View>
              </View>
            ) : null}
            {!showInviteClaimUi && orderedRounds.length ? (
              <View style={styles.roundSectionsCard}>
                <Text style={styles.roundSectionsTitle}>{t('tourLeaderboard.roundSectionsTitle')}</Text>
                {orderedRounds.map((round, index) => {
                  const rows = roundRowsByRoundId[round.id] ?? [];
                  const roundNumber = roundIndexById.get(round.id) ?? index + 1;
                  const roundName = round.name ?? round.courseName ?? t('tourLeaderboard.roundFallback');
                  const roundDate = formatRoundDate(round.createdAt);

                  return (
                    <View key={`${round.id}-section`} style={styles.roundSectionCard}>
                      <View style={styles.roundSectionHeader}>
                        <Text style={styles.roundSectionTitle}>
                          {t('tourLeaderboard.roundSectionTitle', { round: roundNumber, name: roundName })}
                        </Text>
                        {roundDate ? <Text style={styles.roundSectionMeta}>{roundDate}</Text> : null}
                      </View>
                      <View style={styles.roundSectionTable}>
                        <View style={styles.roundSectionTableHeader}>
                          <Text style={[styles.roundSectionHeaderText, styles.roundSectionPlayerHeader]}>
                            {t('tourLeaderboard.player')}
                          </Text>
                          <Text style={styles.roundSectionHeaderText}>{t('tourLeaderboard.roundPoints')}</Text>
                        </View>
                        {rows.length ? (
                          rows.map((row, rowIndex) => (
                            <View
                              key={`${round.id}-${row.playerId}`}
                              style={[
                                styles.roundSectionTableRow,
                                rowIndex % 2 === 0 ? styles.roundSectionTableRowEven : styles.roundSectionTableRowOdd,
                              ]}
                            >
                              <Text style={styles.roundSectionPlayerCell} numberOfLines={1}>
                                {row.name}
                              </Text>
                              <Text style={styles.roundSectionPointsCell}>{row.roundPoints}</Text>
                            </View>
                          ))
                        ) : (
                          <Text style={styles.roundSectionEmpty}>{t('tourLeaderboard.roundMembersEmpty')}</Text>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            ) : null}
          </>
        )}
      </ScrollView>
      <Modal visible={resultsOpen} transparent animationType="fade" onRequestClose={() => setResultsOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setResultsOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('tourLeaderboard.resultsTitle')}</Text>
            {resultsLoading ? <ActivityIndicator color={appColors.text.primary} /> : null}
            {resultsError ? <Text style={styles.modalError}>{resultsError}</Text> : null}
            {totalsByHole && totalsByHole.series?.length ? (
              <ResultsGraph
                series={totalsByHole.series}
                players={players}
                holes={totalsByHole.holes ?? []}
                rounds={totalsByHole.rounds ?? []}
                lastHoleIndexByPlayer={totalsByHole.lastHoleIndexByPlayer ?? totalsByHole.LastHoleIndexByPlayer}
              />
            ) : null}
            <View style={styles.modalButtonRow}>
              <GoffyButton
                label={t('tourLeaderboard.resultsTableButton')}
                onPress={() => setResultsTableOpen(true)}
                visualStyle="blue"
                size="compact"
                tone="dark"
                oldButtonStyle={styles.modalSecondaryButton}
                oldTextStyle={styles.modalSecondaryText}
                style={styles.modalActionButton}
                textStyle={styles.modalActionText}
              />
              <GoffyButton
                label={t('tourLeaderboard.resultsClose')}
                onPress={() => setResultsOpen(false)}
                visualStyle="blue"
                size="compact"
                tone="dark"
                oldButtonStyle={styles.modalCloseButton}
                oldTextStyle={styles.modalCloseText}
                style={styles.modalActionButton}
                textStyle={styles.modalActionText}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={resultsTableOpen} transparent animationType="fade" onRequestClose={() => setResultsTableOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setResultsTableOpen(false)}>
          <Pressable style={[styles.modalCard, styles.modalTableCard]} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('tourLeaderboard.resultsTableTitle')}</Text>
            {resultsLoading ? <ActivityIndicator color={appColors.text.primary} /> : null}
            {resultsError ? <Text style={styles.modalError}>{resultsError}</Text> : null}
            {totalsByHole ? (
              <ResultsTable
                series={totalsByHole.series}
                players={players}
                holes={totalsByHole.holes}
                rounds={totalsByHole.rounds}
              />
            ) : null}
            <View style={styles.modalButtonRow}>
              <GoffyButton
                label={t('tourLeaderboard.resultsClose')}
                onPress={() => setResultsTableOpen(false)}
                visualStyle="blue"
                size="compact"
                tone="dark"
                oldButtonStyle={styles.modalCloseButton}
                oldTextStyle={styles.modalCloseText}
                style={styles.modalActionButton}
                textStyle={styles.modalActionText}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
          <Pressable style={[styles.modalCard, styles.roundPickerModalCard]} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('scorecard.selectRoundTitle')}</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.roundPickerOptions}>
                {orderedRounds.map((round) => {
                  const isSelectable = isRoundSelectableByStatus(round.status);
                  return (
                    <Pressable
                      key={round.id}
                      style={[styles.roundPickerOption, !isSelectable ? { opacity: 0.5 } : null]}
                      onPress={() => {
                        if (!effectiveSelectedTourId || !isSelectable) {
                          return;
                        }
                        setRoundPickerOpen(false);
                        router.push(`/round/${round.id}/summary?tourId=${effectiveSelectedTourId}`);
                      }}
                      disabled={!isSelectable}
                    >
                      <Text style={styles.roundPickerOptionTitle}>
                        {round.name ?? round.courseName ?? t('scorecard.selectRoundFallback')}
                      </Text>
                      <Text style={styles.roundPickerOptionSubtitle}>
                        {t('tourLeaderboard.roundSectionTitle', {
                          round: roundIndexById.get(round.id) ?? '-',
                          name: round.name ?? round.courseName ?? t('scorecard.selectRoundFallback'),
                        })}
                        {!isSelectable ? ' (Not started)' : ''}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </ScrollView>
            <View style={styles.modalButtonRow}>
              <GoffyButton
                label={t('tourLeaderboard.resultsClose')}
                onPress={() => setRoundPickerOpen(false)}
                size="compact"
                tone="dark"
                oldButtonStyle={styles.modalCloseButton}
                oldTextStyle={styles.modalCloseText}
                style={styles.modalActionButton}
                textStyle={styles.modalActionText}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ImageBackground>
  );
}
